import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Barrière d'erreur.
 *
 * Sans elle, la moindre exception de rendu démonte l'intégralité de l'arbre
 * React : l'utilisateur se retrouve devant une page blanche, sans indication.
 * On isole la panne, on affiche un message exploitable et on laisse un moyen
 * de repartir.
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Trace complète en console pour le diagnostic
    console.error('[ErrorBoundary] Rendu interrompu :', error);
    console.error('[ErrorBoundary] Pile de composants :', info.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // Signature typique d'une modification du DOM par un tiers (extension de
    // navigateur, traduction automatique) : on l'indique explicitement, c'est
    // la cause la plus fréquente et la plus déroutante.
    const domMutation = /insertBefore|removeChild|not a child of this node/i.test(
      error.message || ''
    );

    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center p-6">
        <div className="card bg-base-100 shadow-xl max-w-lg w-full">
          <div className="card-body">
            <h2 className="card-title text-error">Une erreur est survenue</h2>
            <p className="text-sm text-base-content/70">
              L'affichage a été interrompu. Vos données ne sont pas affectées.
            </p>

            {domMutation && (
              <div className="alert alert-warning mt-2 py-2">
                <span className="text-xs">
                  Cette erreur provient généralement d'une extension de navigateur ou de la
                  traduction automatique de la page, qui modifient le contenu à l'insu de
                  l'application. Essayez en navigation privée ou en désactivant la traduction.
                </span>
              </div>
            )}

            <details className="mt-3">
              <summary className="cursor-pointer text-xs text-base-content/60">
                Détail technique
              </summary>
              <pre className="mt-2 text-xs bg-base-200 p-3 rounded overflow-x-auto whitespace-pre-wrap">
                {error.message}
              </pre>
            </details>

            <div className="card-actions justify-end mt-4">
              <button className="btn btn-ghost btn-sm" onClick={this.handleHome}>
                Accueil
              </button>
              <button className="btn btn-warning btn-sm" onClick={this.handleReload}>
                Recharger la page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
